import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useAuth } from '../context/AuthContext';
import AuthLayout from '../components/AuthLayout';
import AuthLogo from '../components/AuthLogo';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    minHeight: 48,
    '& fieldset': { borderColor: '#d9d9d9' },
    '&:hover fieldset': { borderColor: '#9e9e9e' },
    '&.Mui-focused fieldset': { borderColor: '#79A82A', borderWidth: 1 },
  },
  '& .MuiOutlinedInput-input': {
    py: 1.5,
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: '#3a3a3a',
    opacity: 1,
  },
};

const labelSx = {
  fontSize: '16px',
  fontWeight: 700,
  color: '#1a1a1a',
  mb: 1,
  display: 'block',
};

const iconSx = { fontSize: 24, color: '#6b6b6b' };

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    setError('');
    setLoading(true);

    if (!username.trim() || !password.trim()) {
      setError(t('login.errorBothFields'));
      setLoading(false);
      return;
    }

    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      let errorMessage = t('login.errorCredentials');

      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = t('login.errorConnection');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mb: { xs: 8, md: 10 } }}>
          <AuthLogo />
        </Box>

        {/* Heading */}
        <Typography
          component="h1"
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '0.01em',
            color: '#1a1a1a',
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          {t('login.welcomeBack')}
        </Typography>
        <Typography
          sx={{ fontSize: '16px', color: '#6b6b6b', textAlign: 'center', mb: { xs: 8, md: 10 } }}
        >
          {t('login.signInSubtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <Typography component="label" htmlFor="login-email" sx={labelSx}>
            {t('login.emailLabel')}
          </Typography>
          <TextField
            id="login-email"
            fullWidth
            placeholder={t('login.enterEmail')}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            autoFocus
            autoComplete="username"
            sx={{ ...fieldSx, mb: 3 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 2 }}>
                    <PersonOutlineIcon sx={iconSx} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Typography component="label" htmlFor="login-password" sx={labelSx}>
            {t('login.passwordLabel')}
          </Typography>
          <TextField
            id="login-password"
            fullWidth
            placeholder={t('login.enterPassword')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            autoComplete="current-password"
            sx={{ ...fieldSx, mb: 1.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 2 }}>
                    <LockOutlinedIcon sx={iconSx} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 22, color: '#6b6b6b' }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 22, color: '#6b6b6b' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 6 }}>
            <Typography
              component={RouterLink}
              to="/forgot-password"
              sx={{
                color: '#79A82A',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('login.forgotPassword')}
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            disableElevation
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              borderRadius: '8px',
              minHeight: 46,
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#222222' },
              '&.Mui-disabled': { backgroundColor: '#000000', color: '#ffffff', opacity: 0.7 },
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : t('login.signIn')}
          </Button>
        </Box>

        <Typography
          component={RouterLink}
          to="/new-member"
          sx={{
            mt: 4,
            color: '#6b6b6b',
            textDecoration: 'none',
            fontSize: '14px',
            '&:hover': { textDecoration: 'underline', color: '#79A82A' },
          }}
        >
          {t('login.notMember')}
        </Typography>

        {/* Language selector */}
        <Select
          value={i18n.language?.split('-')[0] || 'en'}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          size="small"
          variant="standard"
          disableUnderline
          sx={{ mt: 2, fontSize: '14px', color: '#6b6b6b' }}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '0.85rem' }}>
              {lang.nativeName}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </AuthLayout>
  );
};

export default Login;
